// @flow
import { PlayPreviewOnlyButton } from '@components/Track/PlayButton'
import { colors } from '@components/shared'
import { OutlineOnFocusVisible } from '@components/shared/buttons'
import { GoodSansB4, GoodSansH1, GoodSansH3, GoodSansB5 } from '@components/shared/typography'
import { useRouter } from 'next/router'
import * as React from 'react'
import { config } from 'react-awesome-styled-grid'
import styled from 'styled-components'
import type { TrackPropsType } from '../../models/Track'

export function TrackPreview ({
  track,
  slug,
  title,
  description,
  popularity,
  imageUrl,
  isCoverflow,
  isSmall
}: {
  track: TrackPropsType,
  slug: string,
  title: string,
  description: string,
  popularity: number,
  imageUrl: string,
  isCoverflow?: boolean,
  isSmall?: boolean
}): React.Node {
  const router = useRouter()
  const click = React.useCallback((): void => {
    router.push(slug)
  }, [slug])

  return (
    <OutlineWrapper>
      <Container
        onClick={click}
        role="button"
        tabIndex={0}
        alt={`Open ${title}`}
      >
        <img src={imageUrl} alt={title} />
        <Gradient />
        <Content isCoverflow={isCoverflow}>
          <div>
            <Header isSmall={isSmall}>{title}</Header>
            <CopyContainer>
              <Copy isSmall={isSmall}>
                {description}
              </Copy>
              {popularity > 0
                ? <TotalPlays isSmall={isSmall}>{`${popularity.toLocaleString()} plays`}</TotalPlays>
                : ''}
            </CopyContainer>
          </div>
          <PreviewContainer>
            <PlayPreviewOnlyButton {...track} size={isSmall ? 28 : 32} />
            <Preview isSmall={isSmall}>PREVIEW</Preview>
          </PreviewContainer>
        </Content>
      </Container>
    </OutlineWrapper>
  )
}

const Header = styled.p`
  ${(props: any): string => props.isSmall ? GoodSansH3 : GoodSansH1};
`
const CopyContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
`
const Copy = styled.p`
  ${(props: any): string => props.isSmall ? GoodSansB5 : GoodSansB4};
  margin-right: 1em;
  display: inline;
  margin-top: 3px;
`
const TotalPlays = styled.p`
  ${(props: any): string => props.isSmall ? GoodSansB5 : GoodSansB4};
  margin-top: 3px;
  position: relative;
  :before {
    content: "|";
    position: absolute;
    left: -.7em;
  }
`
const Gradient = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 50%;
  background: linear-gradient(to bottom, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.7) 100%);
`
const Content = styled.div`
  align-items: flex-end;
  display: flex;
  justify-content: space-between;
  position: absolute;
  overflow: hidden;
  bottom: 20px;
  left: 18px;
  right: 18px;
  ${(props: any): string => config(props).media.md`
    left: ${(props: any): string => props.isCoverflow ? '20px' : '28px'};
    right: ${(props: any): string => props.isCoverflow ? '20px' : '28px'};
    bottom: ${(props: any): string => props.isCoverflow ? '20px' : '30px'};
  `}
`
const OutlineWrapper = styled.div`
  padding: 2px; /* space for the accessible outline when the Container has keyboard focus */
`
const Container = styled.div`
  background-color: ${colors.midnightBlueTint};
  border-radius: 30px;
  cursor: pointer;
  margin: 0 0 24px;
  padding-top: 100%;
  position: relative;
  width: 100%;
  overflow: hidden;
 ${OutlineOnFocusVisible};
  img {
    bottom: 0;
    border-radius: 30px;
    height: 100%;
    left: 0;
    position: absolute;
    right: 0;
    top: 0;
    width: 100%;
  }
`

const PreviewContainer = styled.div`
  margin: 0 0 0 12px;
  padding-top: 2px; /* needed for accessible focus */
`
const Preview = styled.p`
  ${GoodSansH3};
  color: ${colors.white};
  margin-top: .25rem;
  font-size: ${(props: any): string => props.isSmall ? '7px' : '8px'};
  ${(props: any): string => config(props).media.md`
    font-size: ${(props: any): string => props.isSmall ? '8px' : '9px'};
  `}
  ${(props: any): string => config(props).media.lg`
    font-size: ${(props: any): string => props.isSmall ? '9px' : '10px'};
  `}
`
